import { useState } from "react";
import { useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { useEffect } from "react";
import { getCandidateLink, confirmInterview, PUBLIC_TOKEN } from "@/lib/api";
import { ContactFooter } from "@/components/ui-bits";

const SHIFTS = ["1st Shift", "2nd Shift", "3rd Shift", "Open to Any"];
const REFERRAL_SOURCES = [
  "Recruiting partner",
  "Halfway / recovery house",
  "CareerLink-type organization",
  "Referral (individual)",
  "Applied directly",
  "Other",
];
const NEEDS_ORG = new Set([
  "Recruiting partner",
  "Halfway / recovery house",
  "CareerLink-type organization",
  "Other",
]);

function Pick({ options, value, onChange, testidPrefix }) {
  return (
    <div className="ca-pickrow" data-testid={testidPrefix}>
      {options.map((o) => (
        <button
          key={String(o.v ?? o)}
          type="button"
          className={`ca-pick${value === (o.v ?? o) ? " on" : ""}`}
          onClick={() => onChange(o.v ?? o)}
          data-testid={`${testidPrefix}-${String(o.v ?? o)}`}
        >
          {o.label ?? o}
        </button>
      ))}
    </div>
  );
}

export default function CandidateApply() {
  const { token: tokenParam } = useParams();
  const token = tokenParam || PUBLIC_TOKEN;
  const [data, setData] = useState(null);
  const [nf, setNf] = useState(false);
  const [f, setF] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [shift, setShift] = useState(null);
  const [travelPass, setTravelPass] = useState(null); // true | false | null
  const [referralSource, setReferralSource] = useState("");
  const [referralOrganization, setReferralOrganization] = useState("");
  const [positionInterest, setPositionInterest] = useState("");
  const [ack, setAck] = useState(false);
  const [choice, setChoice] = useState(null); // date iso | "none" | null
  const [busy, setBusy] = useState(false);
  const [photoId, setPhotoId] = useState(null);
  const [workAuth, setWorkAuth] = useState(null);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { getCandidateLink(token).then(setData).catch(() => setNf(true)); }, [token]);

  if (nf) return <div className="mh"><div className="mh-empty">This interview link is not valid.</div></div>;
  if (!data) return <div className="mh"><div className="mh-empty">Loading…</div></div>;

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const infoValid = f.firstName.trim() && f.lastName.trim() && f.email.trim() && f.phone.trim();
  const needsOrg = NEEDS_ORG.has(referralSource);
  const orgValid = !needsOrg || referralOrganization.trim();
  const canSubmit = infoValid && shift && travelPass !== null && referralSource && orgValid && ack && choice !== null;

  const submit = async () => {
    if (!canSubmit || busy) return;
    setErr(""); setBusy(true);
    try {
      const r = (await confirmInterview(token, {
        ...f,
        sessionId: choice && choice.startsWith("special:") ? choice.slice(8) : null,
        sessionDate: choice === "none" || (choice && choice.startsWith("special:")) ? null : choice,
        preferredShift: shift,
        travelPassRequired: travelPass === true,
        referralSource,
        referralOrganization: needsOrg ? referralOrganization.trim() : null,
        positionInterest: positionInterest || null,
        acknowledged: ack,
        photoIdAtInterview: photoId === true ? "Yes" : photoId === false ? "No" : null,
        workAuthDocs: workAuth === true ? "Yes" : workAuth === false ? "No" : null,
      })) || {};
      if (r.checkAvailability) setDone({ mode: "availability" });
      else setDone({ mode: "confirmed", ...r });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  if (done?.mode === "confirmed") return (
    <div className="mh"><div className="mh-wrap ca-wrap">
      <div className="mh-success-ic ca-center"><Check size={28} /></div>
      <h1 className="ca-confirm-title">You're registered with Make It Happen Philly</h1>
      <p className="ca-lead" data-testid="apply-not-offer">Registration is not a job offer and does not guarantee employment.</p>
      <div className="ca-confirm-card" data-testid="apply-confirmed">
        <div className="ca-emp">{done.name}</div>
        {done.assigned ? (
          <>
            <p className="mut" style={{ margin: "8px 0 0", lineHeight: 1.5 }} data-testid="apply-registration-received">
              <strong>Registration received</strong> for the <strong>{done.sessionName}</strong>{done.dateLabel ? ` on ${done.dateLabel}` : ""}. Your interview registration has been received. Please wait for your MIHP confirmation code and final interview details before reporting to the interview location.
            </p>
          </>
        ) : (
          <>
            <p className="mut" style={{ margin: "8px 0 0", lineHeight: 1.5 }}>
              Thank you for registering. Make It Happen Philly will review your registration and contact you
              to confirm your interview date, time, and location.
            </p>
            {done.preferredDateLabel && (
              <p className="mut" style={{ margin: "8px 0 0" }} data-testid="apply-preferred-date">
                Your preferred date: <strong>{done.preferredDateLabel}</strong> — to be confirmed by MIHP.
              </p>
            )}
          </>
        )}
      </div>

      {done.travelPassRequired && (
        <div className="ca-travel" data-testid="apply-travel-pass">
          <h2>Travel Pass Confirmation</h2>
          <p>
            This confirms that you are a registered candidate for a {done.employerName} employment interview
            through Make It Happen Philly. Make It Happen Philly will confirm your interview date, time, and
            location directly with you. If your residential program, caseworker, or other authorized
            representative requires verification, please provide or forward this confirmation to them.
          </p>
        </div>
      )}

      <p className="ca-email-note" data-testid="apply-email-note">
        A confirmation email has been sent to <strong>{done.email}</strong>. You can forward it to your caseworker or program if needed.
      </p>
      <ContactFooter />
    </div></div>
  );

  if (done?.mode === "availability") return (
    <div className="mh"><div className="mh-wrap ca-wrap">
      <div className="mh-success-ic ca-center"><Check size={28} /></div>
      <h1 className="ca-confirm-title" data-testid="apply-availability">Request received</h1>
      <p className="ca-lead">No problem — Make It Happen Philly will follow up with you about your availability. You're still an active candidate. This is not a job offer.</p>
      <ContactFooter />
    </div></div>
  );

  return (
    <div className="mh" data-testid="candidate-apply"><div className="mh-wrap ca-wrap">
      <header className="mh-head">
        <div className="mh-logo"><img src="/mihp-logo.png" alt="Make It Happen Philly" /></div>
        <div className="mh-agency">MAKE IT HAPPEN PHILLY</div>
        <h1 className="mh-headtitle">{data.employerName} Interview Registration</h1>
        <div className="mh-headsub">Interviews are held Mondays at {data.interviewTime}.</div>
        <div className="ca-loc-info" data-testid="apply-location">
          <div className="ca-loc-label">Interview Location</div>
          <div className="ca-loc-name">Hope’s Cookies</div>
          <div className="ca-loc-addr mut">Make It Happen Philly will share the exact interview address when they confirm your interview.</div>
        </div>
        <div className="mh-headsub">Registering is not a job offer.</div>
      </header>

      <label className="mh-lbl">Your information</label>
      <div className="ca-fields">
        <input className="ca-input" placeholder="First name" value={f.firstName} onChange={set("firstName")} data-testid="apply-first" />
        <input className="ca-input" placeholder="Last name" value={f.lastName} onChange={set("lastName")} data-testid="apply-last" />
        <input className="ca-input" type="email" inputMode="email" placeholder="Email (required)" value={f.email} onChange={set("email")} data-testid="apply-email" />
        <input className="ca-input" type="tel" inputMode="tel" placeholder="Mobile phone" value={f.phone} onChange={set("phone")} data-testid="apply-phone" />
      </div>

      <label className="mh-lbl">Which shifts are you available to work?</label>
      <Pick options={SHIFTS} value={shift} onChange={setShift} testidPrefix="apply-shift" />
      <p className="mh-help" data-testid="apply-shift-note">Tell us your availability — final shift assignment happens only after the employer's Hire decision.</p>

      <label className="mh-lbl">Do you require a travel pass from a caseworker or program to attend?</label>
      <Pick
        options={[{ v: true, label: "Yes" }, { v: false, label: "No" }]}
        value={travelPass}
        onChange={setTravelPass}
        testidPrefix="apply-travel"
      />

      <label className="mh-lbl">How did you hear about this?</label>
      <select className="ca-input" value={referralSource} onChange={(e) => setReferralSource(e.target.value)} data-testid="apply-referral-source">
        <option value="">Select…</option>
        {REFERRAL_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      {needsOrg && (
        <input className="ca-input" style={{ marginTop: 10 }} placeholder="Referral organization name"
          value={referralOrganization} onChange={(e) => setReferralOrganization(e.target.value)} data-testid="apply-referral-org" />
      )}

      <label className="mh-lbl">Will you be able to bring a valid photo ID to your interview?</label>
      <Pick options={[{ v: true, label: "Yes" }, { v: false, label: "No" }]} value={photoId} onChange={setPhotoId} testidPrefix="apply-photoid" />

      <label className="mh-lbl">If hired, will you be able to provide acceptable documentation to verify your identity and authorization to work in the United States?</label>
      <p className="mh-help" data-testid="apply-workauth-help">For example: a U.S. passport, or other acceptable documents such as a state-issued photo ID together with a Social Security card or certified birth certificate.</p>
      <Pick options={[{ v: true, label: "Yes" }, { v: false, label: "No" }]} value={workAuth} onChange={setWorkAuth} testidPrefix="apply-workauth" />

      {choice !== "none" ? (
        <>
          <label className="mh-lbl">Choose an available interview session</label>
          <div className="ca-dates" data-testid="apply-dates">
            {(data.specialSessions || []).map((s) => (
              <button key={s.id} className={`ca-date-opt${choice === `special:${s.id}` ? " on" : ""}`} onClick={() => setChoice(`special:${s.id}`)} data-testid={`apply-special-${s.id}`}>
                <span className="ca-date-day">{s.displayName} — {s.label}</span>
                <span className="ca-date-time">{s.time}{s.locationName ? ` · ${s.locationName}` : ""}{s.shiftNeed ? ` · ${s.shiftNeed}` : ""}</span>
              </button>
            ))}
            {data.sessions.map((s) => (
              <button key={s.date} className={`ca-date-opt${choice === s.date ? " on" : ""}`} onClick={() => setChoice(s.date)} data-testid={`apply-date-${s.date}`}>
                <span className="ca-date-day">Monday Interview — {s.label}</span>
                <span className="ca-date-time">{data.interviewTime}</span>
              </button>
            ))}
          </div>
          {(() => {
            const sel = (data.specialSessions || []).find((s) => `special:${s.id}` === choice);
            return sel && sel.candidateInstructions ? (
              <div className="ca-bring" data-testid="apply-session-instructions">
                <h2>What to bring &amp; session details</h2>
                <p style={{ whiteSpace: "pre-wrap" }}>{sel.candidateInstructions}</p>
              </div>
            ) : null;
          })()}
          <button className="ca-neither" onClick={() => setChoice("none")} data-testid="apply-neither">None of these work</button>
        </>
      ) : (
        <div className="ca-neither-panel" data-testid="apply-neither-panel">
          <h2>Need a different interview date?</h2>
          <p className="mut">No problem. Make It Happen Philly will follow up with you about your availability.</p>
          <button className="ca-neither" onClick={() => setChoice(null)}>← Back to dates</button>
        </div>
      )}

      <label className="ca-ack" data-testid="apply-ack">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="apply-ack-check" />
        <span>I understand that registering for an interview does not guarantee employment.</span>
      </label>

      {err && <div className="mh-err" data-testid="apply-error">{err}</div>}
      <button className="mh-save" disabled={!canSubmit || busy} onClick={submit} data-testid="apply-submit">
        {busy ? "Submitting…" : choice === "none" ? "Submit request" : "Confirm interview registration"}
      </button>
    </div></div>
  );
}
